import type { ReactNode } from "react";
import type { Compendium } from "@lancer-vtt/compendium";
import type { Combatant, EncounterState } from "@lancer-vtt/domain";
import type { LoadedOverlay } from "@lancer-vtt/rules";
import type { UseEncounter } from "../useEncounter";
import { setTrackCommand } from "../tracks";
import { buildEndAttackIntent } from "../attackIntent";
import type { ActionDraft } from "../combatInspector";
import { npcLoadout } from "../unitLoadout";
import { TurnControls } from "./TurnControls";
import { UnitSheet, UnitSheetStatuses, type UnitSheetIdentity } from "./UnitSheet";
import { SkirmishChooser } from "./SkirmishChooser";
import { PreparedHoldPanel } from "./inspectorPanels";
import { assertUnmasked } from "./masking";
import { UnitActionColumn } from "./unitActions";
import { LoadoutRows, UnitLoadout } from "./UnitLoadout";
import { unitActionData } from "./useUnitActionModel";
import { npcVitalTracks, UnitSheetVitals } from "./unitVitals";
import { gmReloadRowActions } from "./gmReload";
import { descriptiveStatCells, StatStrip, HaseStatRow } from "./unitStats";
// Type-only — see {@link MechInspectorBody}'s identical import.
import type { PreselectBonus } from "../../../routes/home/DiceApp";

/**
 * The **NPC** PLAY inspector body — the GM's dedicated "hostile control"
 * surface, routed from {@link InspectorBody} when `combatant.kind === "npc"` (mech gets {@link
 * MechInspectorBody}, pilot gets {@link PilotInspectorBody}; deployable/object keep the generic
 * body). It fills the {@link UnitSheet} shell (header with class, role, tier and templates; the
 * state line with END TURN while this NPC is mid-turn; banners) and its shared sections: the
 * vitals ({@link UnitSheetVitals}: HP, a HEAT bar **only** when `base.maxHeat !== undefined` — never
 * a faked one — STRUCT and STRESS pips; −/+ only for a viewer who commands the unit), the stat grid
 * and the NPC's frozen HASE row with dice, the statuses (editable for the GM, `UnitSheetStatuses`), and the
 * loadout ({@link UnitLoadout}: weapons, traits, systems and tech, ABOUT; every state pill off
 * `featureState`). **The NPC's actions are the token command menu's**
 * (`npc-command-menu` 3.2, as the mech sheet since `token-command-menu` Phase 4): the sheet keeps
 * only the REACTION column ({@link UnitActionColumn}, Overwatch as a GM-declared reaction, with the
 * NPC's reaction features read-only under it), the
 * fixed-target {@link SkirmishChooser} the Overwatch take, a declared Overwatch and the advisor arm,
 * and {@link PreparedHoldPanel}, which telegraphs an armed hold to every client and gives the
 * owner/GM RELEASE NOW / DROP. NPCs never carry Overcharge or Brace. The GM's tools are the GM
 * strip at the sheet's foot (`UnitGmStrip`), passed in as `gm`.
 */
export function NpcInspectorBody({
  combatant: c,
  identity,
  state,
  actorId,
  encounter,
  aim,
  setAim,
  onSelfActivate,
  fireAt,
  onDeclineOverwatch,
  canControl,
  gm,
  error,
  compendium,
  overlay,
  loadingOverlay,
  onOpenDice,
}: {
  /**
   * The GM's — or the controlling player's — view of the unit, always the real, unmasked
   * `Combatant`: a hostile NPC a player merely
   * *inspects* (`!isGm && !canControl`) routes to {@link PlayerNpcInspectorBody} instead; this body
   * is reached via `InspectorBody`'s `isGm || canControl` branch, which narrows with
   * {@link assertUnmasked} before calling in — the same pattern {@link MechInspectorBody}/
   * {@link PilotInspectorBody} already use. Verified, not a blind cast: `projectCombatantFor`
   * (`projection.ts`) returns `combatant` identity in BOTH cases this body is reached under, before
   * the disclosure/HP-hiding checks ever run — `if (role === "gm") return combatant;` and
   * `if (viewerSlot !== null && combatant.controllerSlotId === viewerSlot) return combatant;` — so
   * `canControl` (which mirrors that same `controllerSlotId === viewerSlot` test, `CombatApp.tsx`'s
   * `canControl`) implies an identity projection, exactly like the GM branch. `assertUnmasked` is
   * the loud backstop if that were ever violated (a masked/HP-hidden shape throws here rather than
   * silently rendering wrong data).
   */
  combatant: Combatant;
  /** The sheet header's identity, derived once by {@link InspectorBody}. */
  identity: UnitSheetIdentity;
  state: EncounterState;
  actorId: string;
  encounter: UseEncounter;
  /** The single board-wide armed action draft (threaded for the shared {@link InspectorActionBar}). */
  aim: ActionDraft | null;
  setAim: (a: ActionDraft | null) => void;
  /** Fire an activation with no spatial target (an undeclared `choose` raises the prompt modal). */
  onSelfActivate: (actionId: string) => void;
  /** Fire the armed action at a target id — the host's `fireAt`, for the {@link SkirmishChooser}'s Overwatch-take FIRE button. */
  fireAt: (targetId: string) => void;
  /** The Overwatch take's explicit decline (the {@link SkirmishChooser}'s ×): the only local gesture that answers the checkpoint on the server. */
  onDeclineOverwatch?: () => void;
  /** Whether the viewer may command this unit (the affordance mirror of the server gate) — HP read-only when false. */
  canControl: boolean;
  /** The GM strip (`UnitGmStrip`), built by {@link InspectorBody} for the GM's sheet only. */
  gm?: ReactNode;
  error: string | null;
  compendium: Compendium;
  /** The merged action overlay a granted row's `resolvable` is derived against. */
  overlay: LoadedOverlay;
  /** True until {@link overlay}'s sidecar fetch has settled — see {@link CompendiumState.loadingOverlay}. */
  loadingOverlay: boolean;
  /** Open COMBAT.app's DICE.app window pre-filled, for the HASE row's roll buttons. Required-nullable, as on {@link MechInspectorBody}. */
  onOpenDice: ((preselect: PreselectBonus) => void) | null;
}) {
  // This body carries NO masked-render branch: a GM's own view of an NPC is never masked
  // (`projectCombatantFor`'s `role === "gm"` identity-always return), so such a branch would be dead
  // code reading as live protection — the real masked/locked surface a hostile-disclosure-limited viewer sees is
  // now {@link PlayerNpcInspectorBody}, reached via `InspectorBody`'s `!isGm && !canControl` branch
  // (hostile-only) instead of a branch inside this component. `combatant` here is a
  // plain `Combatant` (verified via
  // {@link assertUnmasked} at the call site), so every read below is unconditionally safe.

  const setTrack = (kind: "hp" | "heat", value: number) =>
    encounter.dispatch(setTrackCommand({ actorId, combatantId: c.id, kind, value }));

  // The descriptive mini-stats a GM eyeballs, straight off the frozen base — the shared
  // `descriptiveStatCells` the mech/pilot bodies use, so the strip can't drift between them.
  // `grit: false` (tracker D4): an NPC has no pilot and no GRIT — its `base.attackBonus` is the
  // class's per-tier `attack_bonus` (`spawn/npc.ts`), a different quantity that must not wear a GRIT
  // label. Its HASE row does roll: `spawnNpc` freezes all four axes, and the GM rolls the NPC's
  // saves with them (`unit-sheet-redesign` planning call). Note `base.sensors` is genuinely OPTIONAL here —
  // `spawnNpc` sets it only when the class's sensor range is > 0 — so that cell can read as absent.
  const stats = descriptiveStatCells(c.base, { grit: false });

  // The loadout: weapons, then traits, systems and tech, then ABOUT; the reaction features apart,
  // for the REACTIONS group. Every state pill reads `featureState`, as the command menu's rows do.
  const loadout = npcLoadout({ combatant: c, state, compendium, overlay, loadingOverlay });

  // The derivation — the economy mirror, the row views and the status block reasons — is the unit's
  // one action model ({@link unitActionData}), the same one the token command menu reads; the sheet
  // keeps only its REACTION column.
  const model = unitActionData({ combatant: c, state, actorId, isGm: encounter.isGm, canControl, compendium, overlay, loadingOverlay });
  const { economy, isActiveActor, reactionActions, statusBlockReason, held } = model;
  const { reactOpen, reactAvail } = economy;

  // The header END TURN, the same `end_turn` the HUD's ACTIVE chip emits, shown only while this NPC
  // is mid-turn (`isActiveActor` is `end_turn`'s own legality). `canControl` mirrors the server
  // ownership gate (`controllerSlotId === actorId`, or GM).
  const endTurn = () => actorId && encounter.dispatch({ type: "end_turn", actorId, combatantId: c.id });

  const isArmed = aim !== null && aim.attackerId === c.id;
  // End the open attack lock early: the fixed-target chooser's STOP FIRING.
  const endAttack = () => encounter.sendIntent(buildEndAttackIntent(c.id));
  // Overwatch — a GM-DECLARED reaction dispatch, not the
  // passive "prompted when hit" shape Brace uses: arms the SAME skirmish draft (weapon/profile/accuracy
  // + board-click-a-target), tagged `reaction: true` so `fireAt` sends `request_attack` with the
  // `reaction` flag set — the shipped `checkOverwatchRange` + reaction-slot + "overwatch" 1/round cap
  //  gate it exactly as the CLI's `--overwatch` flag does. Distinguished from a plain
  // Skirmish arm by `aim.reaction === true`; expands the SAME `SkirmishChooser` (its title reads
  // OVERWATCH when armed this way).
  const overwatchArmed = isArmed && aim?.mode === "skirmish" && aim.reaction === true;
  const armOverwatch = () =>
    setAim(overwatchArmed ? null : { attackerId: c.id, mode: "skirmish", weaponId: "", profileId: "", accuracy: 0, reaction: true });
  // The armed hold's RELEASE NOW / DROP ({@link PreparedHoldPanel}); the command menu's Prepare card composes it.
  const releaseArmed = isArmed && aim?.mode === "release";
  const armRelease = () => setAim(releaseArmed ? null : { attackerId: c.id, mode: "release" });
  const dropHeld = () => actorId && encounter.dispatch({ type: "drop_prepare", actorId, combatantId: c.id });

  return (
    <UnitSheet
      identity={identity}
      gm={gm}
      className="unit-sheet-control"
      stateLine={
        // The state line, END TURN (a pass on a READY unit), and the GM's START TURN on a player's unit
        // (`TurnControls`). There is no ACTIVATE TURN: any other action on a ready unit starts its
        // turn through the pre-activation fold (`token-command-menu` 2c).
        <TurnControls combatantId={c.id} state={state} actorId={actorId} encounter={encounter} canControl={canControl} isActiveActor={isActiveActor} onEndTurn={endTurn} noun="NPC" />
      }
      error={error}
      hold={
        held && (
          <PreparedHoldPanel
            held={held}
            weapons={c.base.weapons ?? []}
            releaseArmed={releaseArmed}
            canControl={canControl}
            onArmRelease={armRelease}
            onDrop={dropHeld}
          />
        )
      }
    >
      <UnitSheetVitals {...npcVitalTracks(c)} onSetTrack={canControl ? setTrack : null} disabled={!actorId} />

      {/* The stat grid carries nothing rollable for an NPC — no GRIT cell (its `attackBonus` is the
          class's per-tier attack bonus, not a pilot's grit) — so it passes `null` out loud. */}
      <StatStrip cells={stats} onOpenDice={null} />
      <HaseStatRow base={c.base} onOpenDice={onOpenDice} />

      <UnitSheetStatuses combatant={c} edit={encounter.isGm ? { state, actorId, dispatch: encounter.dispatch } : null} />

      {/* The Overwatch take (`CombatApp`'s `startOverwatchTake`), the GM's declared Overwatch
          (the REACTION column) and the GM advisor all arm a Skirmish with a fixed target or as a
          reaction, and none comes from the command menu, so their chooser (weapon pick + FIRE)
          stays on the sheet. Every other action is the token command menu's
          (`npc-command-menu` 3.2, as the mech sheet in `token-command-menu` Phase 4). */}
      {canControl && isArmed && aim !== null && aim.mode === "skirmish" && (aim.reaction === true || aim.fixedTargetId !== undefined) && (
        <div className="unit-sheet-armed-wrap">
          <SkirmishChooser combatant={assertUnmasked(c)} state={state} aim={aim} setAim={setAim} onEndAttack={endAttack} onFire={fireAt} onDeclineOverwatch={onDeclineOverwatch} />
        </div>
      )}
      {/* The loadout (4.3) and the REACTIONS group (4.4): the core Overwatch stays a clickable row
          until the reactions-in-menu follow-up (Decision 2); the NPC's reaction features (Superhot)
          are read-only rows under it. */}
      <UnitLoadout
        groups={loadout.groups}
        rowActions={gmReloadRowActions({ combatant: assertUnmasked(c), actorId, isGm: encounter.isGm, dispatch: encounter.dispatch })}
        reactions={
          <>
            <UnitActionColumn
              title="REACTION"
              tone="react"
              actions={reactionActions}
              combatantId={c.id}
              available={reactOpen}
              availLabel={reactAvail}
              canControl={canControl}
              onOverwatch={armOverwatch}
              statusBlockReason={statusBlockReason}
            />
            <LoadoutRows rows={loadout.reactions} />
          </>
        }
      />
    </UnitSheet>
  );
}
